import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { LogOut, ChevronDown, KeyRound, Moon, Sun, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { getSupabase } from '@/lib/supabase'
import { describeError } from '@/lib/describe-error'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSidebarCollapsed } from '@/lib/hooks/use-sidebar'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'

export function Topbar() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { theme, toggle: toggleTheme } = useTheme()
  const { collapsed, toggle: toggleSidebar } = useSidebarCollapsed()
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null)
  const [pwOpen, setPwOpen] = useState(false)
  const menuBtnRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const initials = (user?.email ?? '?').slice(0, 2).toUpperCase()

  // Mở menu qua PORTAL + position:fixed (thoát mọi stacking context của layout —
  // page-header sticky z cao ở các trang không còn đè lên menu). Vị trí neo dưới nút.
  function openMenu() {
    const r = menuBtnRef.current?.getBoundingClientRect()
    if (r) setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right })
    setMenuOpen(true)
  }

  // Đóng khi click ngoài / Escape.
  useEffect(() => {
    if (!menuOpen) return
    function onDocPointer(e: MouseEvent) {
      if (
        menuRef.current?.contains(e.target as Node) ||
        menuBtnRef.current?.contains(e.target as Node)
      )
        return
      setMenuOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDocPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  async function onSignOut() {
    try {
      await signOut()
      toast.success('Đã đăng xuất')
      navigate('/login', { replace: true })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Lỗi đăng xuất')
    }
  }

  return (
    <header className="relative z-40 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-4">
      {/* Thu gọn / mở rộng sidebar — chỉ hiện ở md+ (mobile không render sidebar). */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleSidebar}
        aria-label={collapsed ? 'Mở rộng menu' : 'Thu gọn menu'}
        aria-pressed={collapsed}
        title={collapsed ? 'Mở rộng menu' : 'Thu gọn menu'}
        className="hidden md:inline-flex"
      >
        {collapsed ? (
          <PanelLeftOpen className="size-4" />
        ) : (
          <PanelLeftClose className="size-4" />
        )}
      </Button>

      {/* <div className="relative flex-1 max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Tìm đơn, khách hàng, sản phẩm... (Ctrl K)"
          className="pl-9"
        />
      </div> */}

      <div className="ml-auto flex items-center gap-2">
        {/* Toggle sáng / tối. */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Chuyển sáng' : 'Chuyển tối'}
          title={theme === 'dark' ? 'Chế độ sáng' : 'Chế độ tối'}
        >
          {theme === 'dark' ? (
            <Sun className="size-4" />
          ) : (
            <Moon className="size-4" />
          )}
        </Button>

        <div className="relative">
          <Button
            ref={menuBtnRef}
            variant="ghost"
            size="sm"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => (menuOpen ? setMenuOpen(false) : openMenu())}
            className="!gap-2"
          >
            <span className="flex size-7 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
              {initials}
            </span>
            <span className="hidden text-sm font-medium text-fg-muted md:inline">
              {user?.email ?? 'Khách'}
            </span>
            <ChevronDown className="size-3.5 text-fg-subtle" />
          </Button>

          {menuOpen &&
            menuPos &&
            createPortal(
              <div
                ref={menuRef}
                role="menu"
                style={{ position: 'fixed', top: menuPos.top, right: menuPos.right }}
                className="z-50 w-48 overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
              >
                <div className="border-b border-border px-4 py-3">
                  <div className="text-xs text-fg-subtle">Đăng nhập với</div>
                  <div className="truncate text-sm font-medium text-fg">
                    {user?.email ?? '—'}
                  </div>
                </div>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-fg-muted hover:bg-surface-muted"
                  onClick={() => {
                    setMenuOpen(false)
                    setPwOpen(true)
                  }}
                >
                  <KeyRound className="size-4" />
                  Đổi mật khẩu
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-fg-muted hover:bg-surface-muted"
                  onClick={() => {
                    setMenuOpen(false)
                    void onSignOut()
                  }}
                >
                  <LogOut className="size-4" />
                  Đăng xuất
                </button>
              </div>,
              document.body,
            )}
        </div>
      </div>

      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </header>
  )
}

// Tự đổi mật khẩu — mọi user, dùng session của CHÍNH MÌNH
// (supabase.auth.updateUser). KHÔNG cần backend/service-role, KHÔNG gate quyền.
function ChangePasswordDialog({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) {
  const [pw, setPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)

  const tooShort = pw.length < 6
  const mismatch = confirm.length > 0 && confirm !== pw

  async function handleSave() {
    if (tooShort || pw !== confirm) return
    setSaving(true)
    try {
      const { error } = await getSupabase().auth.updateUser({ password: pw })
      if (error) throw error
      toast.success('Đã đổi mật khẩu')
      setPw('')
      setConfirm('')
      onClose()
    } catch (err) {
      toast.error(describeError(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Đổi mật khẩu" size="sm">
      <div className="space-y-3">
        <div>
          <Label htmlFor="cp-new">Mật khẩu mới</Label>
          <Input
            id="cp-new"
            type="password"
            autoComplete="new-password"
            value={pw}
            invalid={pw.length > 0 && tooShort}
            onChange={(e) => setPw(e.target.value)}
          />
          <p className="mt-1 text-xs text-fg-subtle">Tối thiểu 6 ký tự.</p>
        </div>
        <div>
          <Label htmlFor="cp-confirm">Nhập lại mật khẩu</Label>
          <Input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            invalid={mismatch}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {mismatch && (
            <p className="mt-1 text-xs text-red-600">Mật khẩu nhập lại không khớp.</p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Huỷ
          </Button>
          <Button
            onClick={handleSave}
            loading={saving}
            disabled={tooShort || pw !== confirm}
          >
            Đổi mật khẩu
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
